import React, { useState } from 'react';
import ToolLayout from '../../components/ToolLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { Clock, Copy } from 'lucide-react';

const TimestampConverterPage = () => {
  const { toast } = useToast();
  const [timestamp, setTimestamp] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [result, setResult] = useState(null);

  const convertFromTimestamp = () => {
    try {
      const ts = parseInt(timestamp);
      const date = new Date(ts * 1000);
      setResult({
        type: 'timestamp',
        utc: date.toUTCString(),
        local: date.toLocaleString(),
        iso: date.toISOString(),
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString()
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Invalid timestamp',
        variant: 'destructive'
      });
    }
  };

  const convertToTimestamp = () => {
    try {
      const date = new Date(dateTime);
      const ts = Math.floor(date.getTime() / 1000);
      setResult({
        type: 'date',
        timestamp: ts,
        milliseconds: date.getTime()
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Invalid date/time',
        variant: 'destructive'
      });
    }
  };

  const getCurrentTimestamp = () => {
    const now = Math.floor(Date.now() / 1000);
    setTimestamp(now.toString());
    setResult(null);
  };

  const copyValue = (value) => {
    navigator.clipboard.writeText(value.toString());
    toast({
      title: 'Copied!',
      description: 'Value copied to clipboard',
    });
  };

  return (
    <ToolLayout
      title="Timestamp Converter"
      description="Convert between Unix timestamp and human-readable date/time. Supports both directions."
      icon={Clock}
    >
      <div className="space-y-8">
        {/* Unix Timestamp to Date */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Unix Timestamp to Date</h3>
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="timestamp">Unix Timestamp (seconds)</Label>
              <Input
                id="timestamp"
                type="number"
                placeholder="e.g., 1704067200"
                value={timestamp}
                onChange={(e) => setTimestamp(e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={convertFromTimestamp}>Convert</Button>
              <Button onClick={getCurrentTimestamp} variant="outline">Now</Button>
            </div>
          </div>

          {result && result.type === 'timestamp' && (
            <div className="space-y-3">
              <Card>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Local Time</p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-sm">{result.local}</p>
                        <Button onClick={() => copyValue(result.local)} variant="ghost" size="sm">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">UTC</p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-sm">{result.utc}</p>
                        <Button onClick={() => copyValue(result.utc)} variant="ghost" size="sm">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">ISO 8601</p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-sm">{result.iso}</p>
                        <Button onClick={() => copyValue(result.iso)} variant="ghost" size="sm">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Date Only</p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-sm">{result.date}</p>
                        <Button onClick={() => copyValue(result.date)} variant="ghost" size="sm">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 dark:border-gray-800"></div>

        {/* Date to Unix Timestamp */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Date to Unix Timestamp</h3>
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="datetime">Date and Time</Label>
              <Input
                id="datetime"
                type="datetime-local"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={convertToTimestamp}>Convert</Button>
            </div>
          </div>

          {result && result.type === 'date' && (
            <div className="space-y-3">
              <Card>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Timestamp (seconds)</p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-lg font-semibold">{result.timestamp}</p>
                        <Button onClick={() => copyValue(result.timestamp)} variant="ghost" size="sm">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Milliseconds</p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-lg font-semibold">{result.milliseconds}</p>
                        <Button onClick={() => copyValue(result.milliseconds)} variant="ghost" size="sm">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </ToolLayout>
  );
};

export default TimestampConverterPage;